import React, { useState, useEffect, useRef } from 'react';
import { BaseAnswer, SurveyQuestionOption } from '@x-guard/xgac-types/xgac';
import styles from './style.module.scss';
import LikertScaleOption from './LikertScaleOption';
import awfulSmile from './assets/awfulSmile.svg';
import notReallySmile from './assets/notReallySmile.svg';
import notBadSmile from './assets/notBadSmile.svg';
import prettyGoodSmile from './assets/prettyGoodSmile.svg';
import greatSmile from './assets/greatSmile.svg';

interface LikertScaleProps {
  onClick: (response: BaseAnswer) => void;
  options: SurveyQuestionOption[];
}

const LikertScale: React.FC<LikertScaleProps> = ({ onClick, options }) => {

  const [hovered, setHovered] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const likertOptions = options.map((option, index) => ({
    ...option,
    id: index,
    src: [awfulSmile, notReallySmile, notBadSmile, prettyGoodSmile, greatSmile][index],
    color: ['awfulOption', 'notReallyOption', 'notBadOption', 'prettyGoodOption', 'greatOption'][index],
  }));

  const handleMouseMove = (e: MouseEvent) => {

    if (containerRef.current) {

      const containerRect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;

      let closestOption = null;
      let minDistance = Infinity;

      likertOptions.forEach((option, index) => {

        const optionCenterX = (containerRect.width / likertOptions.length) * (index + 0.5);
        const distance = Math.abs(mouseX - optionCenterX);

        if (distance < minDistance) {

          minDistance = distance;
          closestOption = option.id;

        }

      });

      setHovered(closestOption);

    }

  };

  const handleMouseLeave = () => {

    setHovered(null);

  };

  useEffect(() => {

    const container = containerRef.current;
    if (container) {

      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseleave', handleMouseLeave);

    }
    return () => {

      if (container) {

        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseleave', handleMouseLeave);

      }

    };

  });

  return (
    <div ref={containerRef} className={styles['likert-scale']}>
      {likertOptions.map((likertOption) => (
        <LikertScaleOption
          key={likertOption.id}
          id={likertOption.id}
          src={likertOption.src}
          color={likertOption.color}
          option={likertOption}
          hovered={hovered}
          onClick={onClick}
          handleMouseEnter={() => setHovered(likertOption.id)}
          handleMouseLeave={handleMouseLeave}
        />
      ))}
    </div>
  );

};

export default LikertScale;
